import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { accessLevelLabel } from "../nav";
import {
  authApi,
  clearTokens,
  getRefreshToken,
  getStoredUser,
  setStoredUser,
  setTokens,
  type AuthUser,
} from "../api/client";

/*
 * Pinia auth store. Tokens + user persist to localStorage (see
 * src/api/client.ts) so a hard refresh keeps the session; the client
 * transparently rotates the refresh token on 401.
 */
export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(getStoredUser());

  const isLoggedIn = computed(() => !!user.value);
  const role = computed(() => user.value?.role || null);
  const isSuperAdmin = computed(() => role.value === "super_admin");
  const isPlatformAdmin = computed(() => role.value === "platform_admin");
  const isPlatformDeveloper = computed(() => role.value === "platform_developer");
  /** Not confined to one organisation — the server's
   *  `scoping.is_platform_principal`, mirrored so the UI can say "every
   *  organisation" rather than naming one. */
  const isPlatformPrincipal = computed(
    () => isSuperAdmin.value || isPlatformAdmin.value || isPlatformDeveloper.value
  );
  const isOrgAdmin = computed(() => role.value === "org_admin");
  const isDeptAdmin = computed(() => role.value === "dept_admin");
  const isAdmin = computed(
    () =>
      isPlatformPrincipal.value || isOrgAdmin.value || isDeptAdmin.value
  );
  // One map, in `src/nav.ts`. See ACCESS_LEVEL_LABELS there for the four
  // copies this replaced and which of them had gone stale.
  const roleLabel = computed(() => accessLevelLabel(role.value));

  // ── Organization context (O3) ─────────────────────────────────────
  /*
   * What this session may ACT AS. Auth payloads only.
   *
   * NOT "my memberships" — the server answers it with
   * `scoping.switchable_organizations`, the same rule
   * `POST /auth/switch-org` grants, so for a super_admin it is every
   * ACTIVE organization and for everyone else it is exactly their live
   * memberships. Do not re-derive or filter it here: an entry in this
   * list is one the endpoint accepts, and that equality is the whole
   * point of it being computed on the server.
   */
  const organizations = computed(() => user.value?.organizations || []);
  /** The org this session is ACTING AS — every list is scoped to it. */
  const activeOrgId = computed(() => user.value?.organization_id || null);
  const activeOrgName = computed(() => user.value?.organization_name || null);
  /** Role inside the active org; differs from the platform role. */
  const activeOrgRole = computed(() => user.value?.organization_role || null);
  /*
   * More than one org to act as ⇒ the switcher is a live control.
   *
   * Deliberately still `length > 1`, and deliberately NOT
   * `|| isSuperAdmin`. The bug this fixed was never in this line: the
   * LIST was wrong (memberships, not what switch-org accepts), so a
   * platform principal belonging to one tenant counted one entry. With
   * the list correct, `> 1` reads literally — "is there more than one
   * thing to switch to?" — and stays right in the case that motivated
   * the label in the first place: a super_admin on a SINGLE-tenant
   * install has exactly one, and must not be shown a menu whose only
   * entry is the one they are already on (plan §O3b).
   */
  const canSwitchOrg = computed(() => organizations.value.length > 1);
  /** Who may reach /organizations at all. */
  const canManageOrgs = computed(() => isSuperAdmin.value || isOrgAdmin.value);
  /*
   * There is deliberately NO `canAdministerOrg(orgId)` here any more.
   *
   * O3b mirrored `scoping.can_administer_organization` in the browser, so
   * the same authorization rule existed in two languages and could drift.
   * Since O3c the server answers it per row — `Organization.can_administer`
   * on `GET /organizations` — computed by the very helper that enforces it.
   * The client's job is to decide what to RENDER from that answer, not to
   * work the answer out. If you need this for an org you have not fetched,
   * fetch it; do not re-derive it.
   */
  // ── Surfaces (the four-surface policy) ────────────────────────────
  /*
   * The server's answer, not a rule restated here. `surfaces` comes off
   * the auth payload, computed by `app/services/surface_policy.py` — the
   * very table the API enforces with — so re-mapping who sees what is one
   * edit on the server and the menu follows without a rebuild.
   *
   * The `role === "super_admin"` fallback covers exactly one case: a
   * session whose stored user predates this field (an open tab across the
   * deploy). It is a RENDERING fallback, and a permissive-looking one is
   * safe because nothing here authorises anything — the middleware
   * refuses the request regardless, and the next `/auth/me` fills the
   * list in properly.
   */
  const surfaces = computed<string[] | null>(() => user.value?.surfaces ?? null);
  function canReach(surface: string | undefined): boolean {
    if (!surface) return true; // no surface declared = any signed-in user
    const list = surfaces.value;
    if (list && list.length) return list.includes(surface);
    return isSuperAdmin.value; // pre-policy payload; server still decides
  }
  /**
   * May this session WRITE on `surface`? — REACH **and** FLOOR, both.
   *
   * R6. The server asks two separate questions of every mutating request
   * and a control that mirrors only one of them is wrong in one of the
   * two directions:
   *
   *   1. REACH — `surface_policy.SURFACE_ROLES`, enforced by the HTTP
   *      middleware before a handler is entered. It is per-install DATA:
   *      a super_admin re-maps it on the Role Mapping page, and a
   *      department role can be granted a surface there too. `canReach`
   *      is the client's copy of the server's own answer (it arrives on
   *      `/auth/me`), so it tracks a tick without another code change.
   *   2. FLOOR — what the endpoint itself still requires underneath.
   *      R5 widened ~40 writes from `require_role()` (super_admin only)
   *      to `require_role(ROLE_ORG_ADMIN)` paired with
   *      `scoping.can_modify_resource` on the row's own organization.
   *      That is a ROLE, in the code, and no Role Mapping tick moves it —
   *      which is exactly why reach alone is not enough: a dept_admin
   *      granted `channels` reaches the page and `POST /channels` still
   *      answers 403.
   *
   * So: reach it AND clear the org_admin floor. Use this for any control
   * whose endpoint is one of those R5 writes — agents, agent tools,
   * knowledge collections, channels, db connections, workflow authoring.
   *
   * NOT for the writes whose floor is `ROLE_DEPT_ADMIN` (api_keys,
   * scheduler, telephony, forms, ask_ai): `isAdmin` is their floor and
   * this would refuse a dept_admin the server accepts. When in doubt,
   * read the floor off `derive_gates` — the Role Mapping page prints it,
   * per endpoint, and it is derived from the routes rather than written
   * down twice.
   */
  function canWrite(surface: string): boolean {
    // R7. This used to be `canReach && canManageOrgs`, and canManageOrgs
    // is super_admin-or-org_admin — so both new platform tiers could
    // reach a page and then find every control on it disabled, which
    // reads as a broken page rather than a permission. A platform
    // principal writes wherever it reaches; WHICH tenant's rows it
    // touches is the server's business and is already decided there.
    return canReach(surface) && (canManageOrgs.value || isPlatformPrincipal.value);
  }
  /** Where to send a session that asked for a page it may not have. */
  const homePath = computed(
    // The server computes this from the surface table (`home_path`), so
    // the fallback only matters before /auth/me has answered. A platform
    // principal lands on the dashboard for the same reason a super_admin
    // does: it is the first surface they hold.
    () =>
      user.value?.home_path ||
      (isPlatformPrincipal.value || isOrgAdmin.value ? "/dashboard" : "/usage")
  );

  const initials = computed(() => {
    const n = user.value?.name || user.value?.email || "?";
    const parts = n.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
  });

  async function login(email: string, password: string): Promise<void> {
    const res = await authApi.login(email, password);
    setTokens(res.access_token, res.refresh_token);
    setStoredUser(res.user);
    user.value = res.user;
  }

  /** Re-sync the profile from the backend (e.g. after role changes). */
  async function refreshMe(): Promise<void> {
    try {
      const me = await authApi.me();
      setStoredUser(me);
      user.value = me;
    } catch {
      /* handled by the client's 401 flow */
    }
  }

  /**
   * Act as a different organization, then RELOAD the whole app.
   *
   * The reload is deliberate, not laziness. Since O2c every list in the
   * product — agents, workflows, forms, knowledge, runs, traces, usage,
   * notifications — is filtered by the caller's active org, and each
   * view fetches once on setup. Swapping the token in place would leave
   * whatever is already on screen showing the PREVIOUS tenant's rows
   * until something happened to refetch it, which is exactly the
   * cross-tenant confusion the O-series exists to prevent. A reload
   * re-runs every one of those fetches with the new token, and costs a
   * few hundred milliseconds on an action taken a handful of times a
   * day. The alternative — a store-wide "org epoch" every view watches —
   * is a correctness guarantee spread across 18 files instead of one.
   */
  async function switchOrg(organizationId: string): Promise<void> {
    const res = await authApi.switchOrg(organizationId);
    setTokens(res.access_token, res.refresh_token);
    setStoredUser(res.user);
    user.value = res.user;
    window.location.reload();
  }

  async function logout(): Promise<void> {
    const rt = getRefreshToken();
    if (rt) {
      try {
        await authApi.logout(rt);
      } catch {
        /* best-effort revoke; clear locally regardless */
      }
    }
    forceLogout();
  }

  /** Clear local state without calling the backend (used on refresh failure). */
  function forceLogout(): void {
    clearTokens();
    setStoredUser(null);
    user.value = null;
  }

  return {
    user,
    isLoggedIn,
    role,
    roleLabel,
    initials,
    isSuperAdmin,
    isPlatformAdmin,
    isPlatformDeveloper,
    isPlatformPrincipal,
    isOrgAdmin,
    isDeptAdmin,
    isAdmin,
    organizations,
    activeOrgId,
    activeOrgName,
    activeOrgRole,
    canSwitchOrg,
    canManageOrgs,
    surfaces,
    canReach,
    canWrite,
    homePath,
    switchOrg,
    login,
    logout,
    forceLogout,
    refreshMe,
  };
});
